/**
 * Sample conversations for the "Preview with sample data" mode of the WhatsApp inbox. Nothing here is real, nothing is stored and
 * nothing is sent: the page only shows these after the person explicitly clicks the preview button, every row is tagged "Sample",
 * and every action is a pure reducer step on local component state (no bff / network). Phones are masked, names are invented.
 */
export type Loc = 'en' | 'ar';
export interface L10n { en: string; ar: string }
export type SampleState = 'ai_active' | 'human_active' | 'closed' | 'snoozed';
export interface SampleMessage { id: string; direction: 'in' | 'out'; text: L10n; at: string }
export interface SampleDraft { id: string; body: L10n | string }
export interface SampleConversation {
  id: string; name: L10n; phoneMasked: string; state: SampleState; unread: boolean; lastMessageAt: string;
  messages: SampleMessage[]; draft: SampleDraft | null;
  qualification: { intent: L10n; budget: L10n; timeframe: L10n; need: L10n; fit: L10n };
  owner: string | null; source: L10n; campaign: L10n; tags: L10n[]; opportunity: boolean;
}

export const PREVIEW_TABS = ['open', 'ai', 'snoozed', 'closed'] as const;
export type PreviewTab = (typeof PREVIEW_TABS)[number];
/** Snoozed exists only here: the API has no snooze state. */
export function inPreviewTab(c: SampleConversation, tab: PreviewTab): boolean {
  return tab === 'open' ? c.state !== 'closed' && c.state !== 'snoozed' : tab === 'ai' ? c.state === 'ai_active' : c.state === tab;
}
export const previewCounts = (rows: readonly SampleConversation[]): Record<PreviewTab, number> => ({
  open: rows.filter((r) => inPreviewTab(r, 'open')).length, ai: rows.filter((r) => inPreviewTab(r, 'ai')).length,
  snoozed: rows.filter((r) => inPreviewTab(r, 'snoozed')).length, closed: rows.filter((r) => inPreviewTab(r, 'closed')).length,
});
export const pick = (v: L10n | string, locale: Loc): string => (typeof v === 'string' ? v : v[locale]);

const l = (en: string, ar: string): L10n => ({ en, ar });
const D = '2026-10-06T';
const m = (id: string, direction: 'in' | 'out', en: string, ar: string, time: string): SampleMessage => ({ id, direction, text: l(en, ar), at: `${D}${time}:00Z` });
const fit = { hi: l('Strong fit', 'ملاءمة قوية'), mid: l('Possible fit', 'ملاءمة محتملة'), lo: l('Weak fit', 'ملاءمة ضعيفة') };
const none = l('', '');

export const SAMPLE_CONVERSATIONS: readonly SampleConversation[] = [
  {
    id: 'sample_1', name: l('Layla Haddad', 'ليلى حداد'), phoneMasked: '+971 5• ••• 4821', state: 'ai_active', unread: true, lastMessageAt: `${D}09:42:00Z`,
    messages: [
      m('s1a', 'in', 'Hi, how much is the monthly social media package?', 'مرحبا، كم سعر باقة التواصل الاجتماعي الشهرية؟', '09:38'),
      m('s1b', 'out', 'Hello Layla! Our packages start with Starter, which includes 12 posts a month. Which platforms do you use?', 'أهلا ليلى! تبدأ باقاتنا بباقة المبتدئ وتشمل 12 منشورا شهريا. ما المنصات التي تستخدمينها؟', '09:39'),
      m('s1c', 'in', 'Instagram and TikTok. Do you also run ads?', 'إنستغرام وتيك توك. هل تديرون الإعلانات أيضا؟', '09:42'),
    ],
    draft: { id: 'sample_draft_1', body: l('Yes, we manage paid ads on both. I can share the Growth package details and a call time this week. Would Thursday work?', 'نعم، ندير الإعلانات المدفوعة على المنصتين. يمكنني إرسال تفاصيل باقة النمو وموعد مكالمة هذا الأسبوع. هل يناسبك يوم الخميس؟') },
    qualification: { intent: l('Pricing enquiry', 'استفسار عن الأسعار'), budget: l('AED 3,000 a month', '3,000 درهم شهريا'), timeframe: l('This month', 'هذا الشهر'), need: l('Social media and paid ads', 'التواصل الاجتماعي والإعلانات المدفوعة'), fit: fit.hi },
    owner: null, source: l('WhatsApp click-to-chat', 'واتساب - محادثة مباشرة'), campaign: l('Autumn social offer', 'عرض الخريف'), tags: [l('Pricing', 'الأسعار'), l('Hot', 'ساخن')], opportunity: false,
  },
  {
    id: 'sample_2', name: l('Omar Nasser', 'عمر ناصر'), phoneMasked: '+966 5• ••• 7340', state: 'human_active', unread: true, lastMessageAt: `${D}09:15:00Z`,
    messages: [
      m('s2a', 'in', 'I would like to book a consultation for next week.', 'أرغب في حجز استشارة الأسبوع القادم.', '09:10'),
      m('s2b', 'out', 'Of course, Omar. I have Tuesday at 11:00 or Wednesday at 15:00.', 'بالتأكيد يا عمر. لدي الثلاثاء الساعة 11:00 أو الأربعاء الساعة 15:00.', '09:13'),
      m('s2c', 'in', 'Wednesday at 15:00 works for me.', 'الأربعاء الساعة 15:00 مناسب لي.', '09:15'),
    ],
    draft: null,
    qualification: { intent: l('Appointment request', 'طلب موعد'), budget: none, timeframe: l('Next week', 'الأسبوع القادم'), need: l('Initial consultation', 'استشارة أولية'), fit: fit.mid },
    owner: 'Sample Agent A', source: l('Website chat button', 'زر المحادثة في الموقع'), campaign: l('Consultation landing page', 'صفحة الاستشارة'), tags: [l('Appointment', 'موعد')], opportunity: false,
  },
  {
    id: 'sample_3', name: l('Maya Rahman', 'مايا رحمن'), phoneMasked: '+971 5• ••• 1190', state: 'ai_active', unread: false, lastMessageAt: `${D}08:50:00Z`,
    messages: [
      m('s3a', 'out', 'Hi Maya, following up on the quotation we sent on Sunday. Do you have any questions?', 'مرحبا مايا، نتابع معك بخصوص عرض السعر الذي أرسلناه يوم الأحد. هل لديك أي أسئلة؟', '08:30'),
      m('s3b', 'in', 'Thanks. Can the website phase start before the branding is done?', 'شكرا. هل يمكن بدء مرحلة الموقع قبل الانتهاء من الهوية؟', '08:50'),
    ],
    draft: null,
    qualification: { intent: l('Quotation follow-up', 'متابعة عرض سعر'), budget: l('AED 25,000 project', 'مشروع بقيمة 25,000 درهم'), timeframe: l('Within 6 weeks', 'خلال 6 أسابيع'), need: l('Website and branding', 'موقع إلكتروني وهوية'), fit: fit.hi },
    owner: 'Sample Agent B', source: l('Email campaign', 'حملة بريد إلكتروني'), campaign: l('Web redesign quote', 'عرض إعادة تصميم الموقع'), tags: [l('Quotation', 'عرض سعر')], opportunity: false,
  },
  {
    id: 'sample_4', name: l('Yusuf Karimi', 'يوسف كريمي'), phoneMasked: '+965 6• ••• 2278', state: 'ai_active', unread: false, lastMessageAt: `${D}08:05:00Z`,
    messages: [
      m('s4a', 'in', 'Does the booking widget work with Arabic and English?', 'هل تعمل أداة الحجز باللغتين العربية والإنجليزية؟', '08:01'),
      m('s4b', 'out', 'Yes, the widget follows the visitor language and supports right-to-left layouts.', 'نعم، تتبع الأداة لغة الزائر وتدعم التخطيط من اليمين إلى اليسار.', '08:05'),
    ],
    draft: null,
    qualification: { intent: l('Product question', 'سؤال عن المنتج'), budget: none, timeframe: none, need: l('Bilingual booking widget', 'أداة حجز ثنائية اللغة'), fit: fit.mid },
    owner: null, source: l('Instagram bio link', 'رابط السيرة في إنستغرام'), campaign: l('Always on', 'مستمرة'), tags: [l('Product', 'منتج')], opportunity: false,
  },
  {
    id: 'sample_5', name: l('Sara Almansoori', 'سارة المنصوري'), phoneMasked: '+971 5• ••• 9035', state: 'human_active', unread: false, lastMessageAt: `${D}07:20:00Z`,
    messages: [
      m('s5a', 'in', 'We are a dental clinic with three branches. Can you handle all our reviews?', 'نحن عيادة أسنان لها ثلاثة فروع. هل يمكنكم إدارة جميع تقييماتنا؟', '07:12'),
      m('s5b', 'out', 'Yes. I will prepare a proposal covering all three branches and send it today.', 'نعم. سأجهز عرضا يشمل الفروع الثلاثة وأرسله اليوم.', '07:20'),
    ],
    draft: null,
    qualification: { intent: l('Multi-branch enquiry', 'استفسار متعدد الفروع'), budget: l('AED 6,000 a month', '6,000 درهم شهريا'), timeframe: l('Next month', 'الشهر القادم'), need: l('Reviews and reputation', 'التقييمات والسمعة'), fit: fit.hi },
    owner: 'Sample Agent A', source: l('Google Business Profile', 'ملف النشاط في جوجل'), campaign: l('Reputation pilot', 'تجربة السمعة'), tags: [l('Clinic', 'عيادة'), l('Proposal', 'عرض')], opportunity: true,
  },
  {
    id: 'sample_6', name: l('Daniel Brooks', 'دانيال بروكس'), phoneMasked: '+44 7• •••• 5512', state: 'snoozed', unread: false, lastMessageAt: `${D}06:40:00Z`,
    messages: [
      m('s6a', 'in', 'Interesting. Let me discuss with my partner and come back next week.', 'مثير للاهتمام. سأناقش الأمر مع شريكي وأعود إليك الأسبوع القادم.', '06:40'),
    ],
    draft: null,
    qualification: { intent: l('Exploring options', 'استكشاف الخيارات'), budget: none, timeframe: l('Next week', 'الأسبوع القادم'), need: l('Lead generation', 'توليد العملاء المحتملين'), fit: fit.mid },
    owner: 'Sample Agent B', source: l('Facebook lead ad', 'إعلان عملاء محتملين في فيسبوك'), campaign: l('Autumn social offer', 'عرض الخريف'), tags: [l('Waiting', 'بانتظار الرد')], opportunity: false,
  },
  {
    id: 'sample_7', name: l('Huda Saleh', 'هدى صالح'), phoneMasked: '+971 5• ••• 6604', state: 'closed', unread: false, lastMessageAt: '2026-10-05T16:10:00Z',
    messages: [
      m('s7a', 'in', 'Thank you, the invoice is paid.', 'شكرا لكم، تم دفع الفاتورة.', '16:05'),
      m('s7b', 'out', 'Received, thank you Huda. Your campaign goes live tomorrow.', 'وصلتنا الدفعة، شكرا هدى. تنطلق حملتك غدا.', '16:10'),
    ],
    draft: null,
    qualification: { intent: l('Payment confirmation', 'تأكيد الدفع'), budget: l('AED 12,000', '12,000 درهم'), timeframe: l('Tomorrow', 'غدا'), need: l('Launch campaign', 'إطلاق حملة'), fit: fit.hi },
    owner: 'Sample Agent A', source: l('Referral', 'توصية'), campaign: l('Launch package', 'باقة الإطلاق'), tags: [l('Won', 'مكتسب')], opportunity: true,
  },
  {
    id: 'sample_8', name: l('Karim Fathy', 'كريم فتحي'), phoneMasked: '+20 10• ••• 3381', state: 'closed', unread: false, lastMessageAt: '2026-10-04T12:30:00Z',
    messages: [
      m('s8a', 'in', 'Do you offer a free trial of the dashboard?', 'هل تقدمون نسخة تجريبية مجانية من لوحة المعلومات؟', '12:20'),
      m('s8b', 'out', 'We offer a guided demo rather than a free trial. Shall I book one for you?', 'نقدم عرضا توضيحيا مرافقا بدلا من تجربة مجانية. هل أحجز لك واحدا؟', '12:30'),
    ],
    draft: null,
    qualification: { intent: l('Product question', 'سؤال عن المنتج'), budget: none, timeframe: none, need: l('Dashboard trial', 'تجربة لوحة المعلومات'), fit: fit.lo },
    owner: null, source: l('Website chat button', 'زر المحادثة في الموقع'), campaign: l('Always on', 'مستمرة'), tags: [l('Low intent', 'اهتمام منخفض')], opportunity: false,
  },
];

export interface PreviewState { rows: SampleConversation[]; lastNote: string | null }
export const initialPreview = (): PreviewState => ({ rows: SAMPLE_CONVERSATIONS.map((c) => ({ ...c, messages: [...c.messages] })), lastNote: null });

export type PreviewAction =
  | { type: 'send'; id: string; body: string; at: string }
  | { type: 'approve'; id: string; body?: string; at: string }
  | { type: 'deleteDraft'; id: string }
  | { type: 'takeover'; id: string }
  | { type: 'assign'; id: string; owner: string }
  | { type: 'createOpportunity'; id: string }
  | { type: 'snooze'; id: string; on: boolean };

/** Every preview action is a pure local state change. Nothing calls the API; nothing is persisted. */
export function previewReducer(s: PreviewState, a: PreviewAction): PreviewState {
  const rows = s.rows.map((c): SampleConversation => {
    if (c.id !== a.id) return c;
    switch (a.type) {
      case 'send': return { ...c, state: c.state === 'ai_active' ? 'human_active' : c.state, unread: false, lastMessageAt: a.at, messages: [...c.messages, { id: `${c.id}_out_${c.messages.length}`, direction: 'out', text: l(a.body, a.body), at: a.at }] };
      case 'approve': {
        if (!c.draft) return c;
        const body = a.body ?? c.draft.body;
        return { ...c, draft: null, unread: false, lastMessageAt: a.at, messages: [...c.messages, { id: `${c.id}_out_${c.messages.length}`, direction: 'out', text: typeof body === 'string' ? l(body, body) : body, at: a.at }] };
      }
      case 'deleteDraft': return { ...c, draft: null };
      case 'takeover': return { ...c, state: 'human_active' };
      case 'assign': return { ...c, owner: a.owner };
      case 'createOpportunity': return { ...c, opportunity: true };
      case 'snooze': return { ...c, state: a.on ? 'snoozed' : 'human_active' };
    }
  });
  return { rows, lastNote: a.type };
}
